/**
 * Detect opaque user ids (UUID / long hex) that should not be shown as display names.
 */
export const looksLikeRawUserId = (value) => {
  const text = String(value || '').trim();
  if (!text) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)
    || /^[0-9a-f]{20,}$/i.test(text);
};

const isUsableDisplayName = (value) => {
  const text = String(value || '').trim();
  if (!text) return false;
  if (text === 'Unknown') return false;
  return !looksLikeRawUserId(text);
};

/**
 * Get user initials with proper fallback logic
 * @param {Object} user - User object
 * @returns {string} User initials (uppercase)
 */
export const getUserInitials = (user) => {
  if (!user) return 'U';
  
  // Get initials from user data with proper fallbacks
  const firstName = (user.firstName || '').trim();
  const lastName = (user.lastName || '').trim();
    
  // If we have both names, use them
  if (firstName && lastName) {
    const initials = (firstName[0] + lastName[0]).toUpperCase();
    return initials;
  }
  
  // Check if we have a 'name' field that might contain both names
  const fullName = (user.name || '').trim();
  if (fullName && !firstName && !lastName && isUsableDisplayName(fullName)) {
    const nameParts = fullName.split(' ').filter(part => part.length > 0);
    if (nameParts.length >= 2) {
      const initials = (nameParts[0][0] + nameParts[1][0]).toUpperCase();
      return initials;
    } else if (nameParts.length === 1) {
      const initial = nameParts[0][0].toUpperCase();
      return initial;
    }
  }
  
  // If we only have first name, use it
  if (firstName) {
    const initial = firstName[0].toUpperCase();
    return initial;
  }
  
  // If we only have last name, use it
  if (lastName) {
    const initial = lastName[0].toUpperCase();
    return initial;
  }
  
  // Fallback to email username if no names available
  if (user.email) {
    const initial = user.email.split('@')[0][0].toUpperCase();
    return initial;
  }

  const id = String(user.id || user.userId || '').trim();
  if (id && !looksLikeRawUserId(id)) {
    return id[0].toUpperCase();
  }
  
  // Final fallback
  return 'U';
};

/**
 * Get user display name with fallbacks
 * @param {Object} user - User object
 * @returns {string} User display name
 */
export const getUserDisplayName = (user) => {
  if (!user) return 'Unknown User';
  
  if (isUsableDisplayName(user.name)) {
    return String(user.name).trim();
  }
  
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  
  if (user.firstName) {
    return user.firstName;
  }
  
  if (user.lastName) {
    return user.lastName;
  }
  
  // Fallback to email
  if (user.email) {
    return user.email.split('@')[0];
  }
  
  return 'Unknown User';
};

/**
 * Resolve author display name from entity records (context, prompt, search hit).
 */
export const getEntityAuthorDisplayName = (entity = {}) => {
  const explicitAuthorName = entity.author?.name && isUsableDisplayName(entity.author.name)
    ? entity.author.name.trim()
    : '';
  const authorUser = {
    ...(entity.author || {}),
    name: explicitAuthorName
      || entity.userName
      || entity.authorName
      || entity.ownerName
      || entity.createdByName
      || entity.creatorName,
    email: entity.author?.email || entity.userEmail || entity.email,
    id: entity.author?.id || entity.userId,
  };
  const resolved = getUserDisplayName(authorUser);
  return looksLikeRawUserId(resolved) ? 'Unknown User' : resolved;
};
